import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Grid, Theme, Typography, Popover, alpha } from "@mui/material";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { SelectField } from "src/components/FormikComponents/SelectField";
import _ from "lodash";
import { ShortCodeChip, IShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
import classNames from "classnames";
import { IAirport, IFlight, IPairing, IUser } from "@vmosolutions/oc-interfaces";
import { LoadingMessage } from "src/components/LoadingMessage/LoadingMessage";
import {
  getBasicPairingInfo,
  getCrewPositionInfo,
  getPairingLogicModifiers,
} from "src/pages/CrewSchedule/HoveredCrewSchedEvents";

import CrewScheduleEventSummaryList, {
  ISummaryItem,
  ISummarySubHeader,
} from "src/components/CrewSchedule/CrewScheduleEventSummaryList";

interface IOption {
  label: string;
  value: string;
  name?: string;
  data?: IAirport | IFlight | IPairing | IUser;
  type?: "pairing" | "flight" | "airport" | "crew";
}

interface IGroupedOption {
  label: string;
  options: IOption[];
}

interface IShortCodeAssignmentProps {
  className?: string;
  getSelectedShortCode?: (shortCode: IShortCodeChip) => void;
  hideInlineEdit?: boolean;
}

// Async search for short codes.
const searchShortCode = async (inputValue: string): Promise<IOption[]> => {
  const response: IOption[] = await AmplifyAPIWrapper.Get(`/pairings/shortCode/${inputValue}`);
  if (Array.isArray(response) && response.length > 0) {
    const newOptions: IOption[] = response.map((code: any) => ({
      label: code.label,
      value: code.value,
      name: null,
      data: null,
      type: "pairing",
    }));
    return newOptions;
  } else {
    return [];
  }
};

/**
 * Format's the option into a component that can be rendered in the select menu.
 * @param option - contains the data for the given item.
 * @param hideInlineEdit - determines whether or not the action button appears.
 */
const formatSelectOptions = (option: IOption, hideInlineEdit: boolean): JSX.Element => {
  const { label, value, type } = option;

  if (type === "pairing") {
    const shortCode: IShortCodeChip = {
      itemId: value,
      code: label,
      type: type as "pairing",
      edit: !hideInlineEdit,
    };
    return (
      <Grid container alignItems="center">
        <ShortCodeChip {...shortCode} />
        <Typography style={{ marginLeft: 10 }} variant="body2" color="textPrimary">
          Pairing
        </Typography>
      </Grid>
    );
  }
  return null;
};

/**
 * ShortCodeAssignment - Component used to search for and assign shortCodes.
 * @param props - Component Properties [[IShortCodeChip]]
 */
export const ShortCodeAssignment = (props: IShortCodeAssignmentProps): JSX.Element => {
  const { className, getSelectedShortCode, hideInlineEdit } = props;
  const [options, setOptions] = useState<IGroupedOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<IOption>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement>();

  // Async Call to get results from both shortCodes and airports and combine them into one options array.
  const fetchResults = useCallback((value: string): void => {
    const getOptions = async (): Promise<void> => {
      setIsLoading(true);
      const shortCodeOptions = await searchShortCode(value);
      const groupedOptions = [{ label: "Pairings", options: shortCodeOptions }];
      setOptions(groupedOptions);
      setIsLoading(false);
    };
    getOptions();
  }, []);

  // Wrapped in a debounce to ensure that a new search isn't run with every character press.
  const handleSearchTextChange = _.debounce((newVal: string): void => {
    // Don't return any results if search string is anything less than 4 characters
    if (newVal?.length < 3) {
      return;
    }
    const value = newVal?.toLowerCase();
    fetchResults(value);
    return;
  }, 1000); // Increase debounced value on this call as it was hitting the backend more often than I wanted. - CC

  useEffect(() => {
    if (selectedOption?.type === "pairing" && selectedOption?.value) {
      if (!selectedOption.data) {
        // GET PAIRING INFO
        const getPairingAction = async (): Promise<void> => {
          const loadedPairing = await AmplifyAPIWrapper.Get<IPairing>(`/pairings/${selectedOption?.value}`, null);
          if (loadedPairing?._id) {
            // Update grouped options and store data (to reduce api calls)
            setOptions((prev) => {
              const shortCodeOptions = prev.find((group) => group.label === "Pairings")?.options ?? [];
              const pairingIndex = shortCodeOptions.findIndex((shortCode) => shortCode.value === loadedPairing?._id);
              if (pairingIndex > -1) {
                shortCodeOptions[pairingIndex].data = loadedPairing;
                setSelectedOption({ ...shortCodeOptions[pairingIndex] });
              }
              return [{ label: "Pairings", options: shortCodeOptions }];
            });
          }
        };
        getPairingAction();
      }
    }
  }, [selectedOption]);

  // Pairing Summary info if selected option if of type pairing.
  const pairingSummaryItems: (ISummaryItem | ISummarySubHeader | null)[] = useMemo(() => {
    if (selectedOption?.type === "pairing" && selectedOption?.data) {
      const displayPairing = selectedOption?.data as IPairing;
      return [
        ...getBasicPairingInfo(displayPairing, "Etc/UTC"),
        ...getCrewPositionInfo(displayPairing?.positions),
        ...getPairingLogicModifiers(displayPairing?.logicModifiers),
      ];
    }
    return [];
  }, [selectedOption]);

  const ShortCodeAssignmentStyles = (theme: Theme): any => ({
    ShortCodeSearch: {
      display: "flex",
      width: "auto",
      marginLeft: 10,
      "& .searchshortcode": {
        width: 200,
      },
      "& .loading_ellipsis": {
        marginTop: 4,
        marginLeft: -25,
      },
      "&:not(.inside_modal) .searchshortcode": {
        "& .input_control .react-select-container .react-select__control": {
          backgroundColor: "transparent",
          borderColor: theme.palette.primary.main,
          "& .react-select__indicators": {
            "& .react-select__indicator-separator": {
              backgroundColor: alpha(theme.palette.primary.contrastText, 0.5),
            },
            "& .react-select__indicator-separator, & .react-select__dropdown-indicator": {
              display: "none",
            },
            "& div": { color: theme.palette.primary.contrastText },
          },
          "& .react-select__placeholder": {
            color: alpha(theme.palette.primary.contrastText, 0.5),
            fontWeight: 300,
          },
        },
        "& .react-select__menu": {
          backgroundColor: alpha(theme.palette.common.black, 0.9),
        },
        "& .input_control .react-select-container .react-select__control.react-select__control--is-focused ": {
          borderColor: alpha(theme.palette.primary.contrastText, 0.5),
        },
        "&:hover": {
          "& .input_control .react-select-container .react-select__control": {
            borderColor: alpha(theme.palette.primary.contrastText, 0.5),
            "& .react-select__indicators": { display: "flex" },
            "& .react-select__placeholder": {},
          },
        },
      },
    },
    PairingPopover: {
      backgroundColor: "transparent",
      boxShadow: "none",
      zIndex: 2000,
      "& .menu": {
        backgroundColor: alpha(theme.custom.contextPopover.backgroundColor, 0.9),
        color: theme.custom.contextPopover.foreGroundColor,
        boxShadow: theme.shadows[5],
        borderRadius: 5,
        paddingTop: 15,
        "& .pairing_id": { margin: "0px 15px", fontSize: 12 },
      },
    },
  });
  return (
    <Grid
      container
      ref={selectRef}
      sx={(theme) => ShortCodeAssignmentStyles(theme).ShortCodeSearch}
      className={className ?? ""}
    >
      <SelectField
        className="searchshortcode"
        name="searchShortCode"
        options={options}
        onInputChange={handleSearchTextChange}
        value={selectedOption}
        onChange={(option: IOption) => {
          setSelectedOption(option);
          if (!option) {
            getSelectedShortCode(null);
            return;
          }
          // setPairingSummaryOpen(option?.type === "pairing" ? true : false);
          if (getSelectedShortCode && option?.type === "pairing") {
            const shortCode: IShortCodeChip = option
              ? { itemId: option.value, code: option.label, type: "pairing", edit: !hideInlineEdit }
              : null;
            getSelectedShortCode(shortCode);
          }
        }}
        formatOptionLabel={(option: any) => formatSelectOptions(option, hideInlineEdit)}
        getOptionLabel={(option: IOption) => `${option.label} ${option.name}`}
        placeholder="Search..."
        noOptionsMessage={(): null => null}
        isClearable={true}
      />
      {isLoading && (
        <div className="loading_ellipsis">
          <LoadingMessage message="" />
        </div>
      )}

      <Popover
        sx={(theme) => ShortCodeAssignmentStyles(theme).PairingPopover}
        style={{ position: "inherit" }}
        anchorEl={selectRef?.current}
        anchorReference="anchorPosition"
        anchorPosition={{ top: 0, left: 360 }}
        open={pairingSummaryItems?.length > 0}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock={true}
      >
        <div className="menu">
          <CrewScheduleEventSummaryList hideTitle={true} items={pairingSummaryItems} />
        </div>
      </Popover>
    </Grid>
  );
};
